import { ComponentPropsWithoutRef, useEffect, useState } from "react"
import {
  DAY_MS,
  HOUR_MS,
  MINUTE_MS,
  SECOND_MS,
} from "shared/utils/formatTime"

import countdownStyles from "./Countdown.module.css"

export const countdownVariants = ["rose", "overlay"] as const

export const countdownSizes = ["md", "lg"] as const

export type CountdownProps = ComponentPropsWithoutRef<"time"> & {
  /**
   * The date (in ms) to count down towards.
   * If this date is an hour from now, this component would show 0d 1h 0m 0s, for example.
   * If this date is before the current time, this component will display 0d 0h 0m 0s.
   */
  countdownDateMs: number

  /**
   * Indicates the colour/variant of the Countdown
   *
   * @default neutral
   */
  variant?: (typeof countdownVariants)[number]

  /**
   * Indicates the size of the Countdown
   *
   * @default md
   */
  size?: (typeof countdownSizes)[number]
}

export const Countdown = ({
  className,
  countdownDateMs,
  variant = "rose",
  size = "md",
  ...props
}: CountdownProps) => {
  const [currentTime, setCurrentTime] = useState(() => Date.now())

  const msDifference = countdownDateMs - currentTime

  let days = 0
  let hours = 0
  let minutes = 0
  let seconds = 0

  if (msDifference > 0) {
    days = Math.floor(msDifference / DAY_MS)
    hours = Math.floor((msDifference % DAY_MS) / HOUR_MS)
    minutes = Math.floor((msDifference % HOUR_MS) / MINUTE_MS)
    seconds = Math.floor((msDifference % MINUTE_MS) / SECOND_MS)
  }

  useEffect(() => {
    const onSecondElapsed = () => {
      setCurrentTime(Date.now())
    }
    const intervalFn = window.setInterval(onSecondElapsed, 1000)
    return () => {
      window.clearInterval(intervalFn)
    }
  }, [])

  return (
    <time
      className={`${countdownStyles.countdown} ${className}`}
      data-variant={variant}
      data-size={size}
      {...props}
    >
      <span className={countdownStyles.countdownNumber}>{days}d</span>
      <span className={countdownStyles.countdownNumber}>{hours}h</span>
      <span className={countdownStyles.countdownNumber}>{minutes}m</span>
      <span className={countdownStyles.countdownNumber}>{seconds}s</span>
    </time>
  )
}
